// @ts-check

/**
 * @file Renders a reusable form select field.
 */

import {useForm} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {View} from "react-native-propforge"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"

import Select from "../select"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{text: string, value: string}} FormSelectOption */
/** @typedef {{page?: number, searchValue?: string, values?: (string | number)[]}} FormSelectOptionsLoaderArgs */
/** @typedef {{options: FormSelectOption[], page?: number, pageSize?: number, totalCount?: number}} FormSelectOptionsResult */
/** @typedef {(args?: FormSelectOptionsLoaderArgs) => Promise<FormSelectOption[] | FormSelectOptionsResult> | FormSelectOption[] | FormSelectOptionsResult} FormSelectOptionsLoader */
/** @typedef {(...args: unknown[]) => unknown} FormSelectChangeHandler */
/** @typedef {{[key: string]: unknown, defaultValue?: string, label: string, name: string, onChangeValue?: FormSelectChangeHandler, options: FormSelectOption[] | FormSelectOptionsLoader, placeholder?: string, search?: boolean, testID: string}} FormSelectProps */

/**
 * @typedef {object} FormSelectState
 * @property {string} selectedValue Currently selected option value.
 */

/**
 * Reusable form select powered by HayaSelect.
 * @augments {ShapeComponent<FormSelectProps, FormSelectState>}
 */
class FormSelect extends ShapeComponent {
  /** @type {ReturnType<typeof useForm> | null} */
  form = null
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {boolean} */
  initialValuePrimed = false
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = propTypesExact({
    defaultValue: PropTypes.string,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    onChangeValue: PropTypes.func,
    options: PropTypes.oneOfType([
      PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired
        })
      ),
      PropTypes.func
    ]).isRequired,
    placeholder: PropTypes.string,
    search: PropTypes.bool,
    testID: PropTypes.string.isRequired
  })
  static defaultProps = {
    defaultValue: undefined,
    onChangeValue: undefined,
    placeholder: undefined,
    search: undefined
  }
  /** @type {FormSelectState} */
  state = {
    selectedValue: ""
  }

  /** Initializes form synchronization. */
  setup() {
    const selectedValue = this.initialSelectedValue(
      this.p.defaultValue,
      this.p.options
    )
    this.form = useForm()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    if (!this.initialValuePrimed) {
      this.initialValuePrimed = true
      this.state.selectedValue = selectedValue
    }
    useEffect(() => {
      if (this.form) {
        this.form.setValue(
          this.p.name,
          this.initialSelectedValue(this.p.defaultValue, this.p.options)
        )
      }
    }, [])
    const defaultValue = this.p.defaultValue
    const fieldName = this.p.name
    const options = this.p.options
    const staticOptions = Array.isArray(options) ? options : undefined
    const nextDefaultValue = this.initialSelectedValue(defaultValue, staticOptions || [])

    useEffect(() => {
      this.s.selectedValue = nextDefaultValue

      if (this.form) {
        this.form.setValue(fieldName, nextDefaultValue)
      }
    }, [defaultValue, fieldName, nextDefaultValue])
  }

  /**
   * Resolves the initial selected value.
   * @param {string | undefined} defaultValue Default value from props.
   * @param {FormSelectOption[] | FormSelectOptionsLoader} options Available options.
   * @returns {string} Resolved value.
   */
  initialSelectedValue(defaultValue, options) {
    if (typeof defaultValue === "string" && defaultValue.length > 0) {
      return defaultValue
    }
    if (!Array.isArray(options)) return ""

    return options[0] && typeof options[0].value === "string"
      ? options[0].value
      : ""
  }

  /**
   * Applies a select value change.
   * @param {(string | number)[] | string | number | undefined} value Selected value.
   * @returns {void} Nothing.
   */
  onChangeValue = (value) => {
    let selectedOptionValue = ""
    if (Array.isArray(value) && value.length > 0) {
      selectedOptionValue = String(value[0])
    } else if (typeof value === "string" || typeof value === "number") {
      selectedOptionValue = String(value)
    }
    this.s.selectedValue = selectedOptionValue
    if (this.form) {
      this.form.setValue(this.p.name, selectedOptionValue)
    }
    this.p.onChangeValue?.(value)
  }

  /**
   * Loads options while honoring HayaSelect selected-value lookups for simple callback option sources.
   * @param {FormSelectOptionsLoaderArgs} [args] HayaSelect loader args.
   * @returns {Promise<FormSelectOption[] | FormSelectOptionsResult>} Options result.
   * @rejects {Error} When the options prop has an unsupported type.
   */
  loadOptions = async (args) => {
    if (Array.isArray(this.p.options)) {
      return filterOptionsForValues(this.p.options, args?.values)
    }
    if (typeof this.p.options !== "function") {
      throw new Error(`Expected ${this.p.name} options to be an array or function`)
    }
    const result = await this.p.options(args)

    return filterOptionsResultForValues(result, args?.values)
  }

  /**
   * Renders the form select field.
   * @returns {ReactNode} Select field content.
   */
  render() {
    const {label, options, placeholder, search, testID} = this.p
    const {selectedValue} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    let currentSelectedValue = selectedValue
    if (Array.isArray(options) && !options.some((option) => option.value === selectedValue)) {
      currentSelectedValue = ""
    }
    /** @type {(string | number)[] | undefined} */
    const selectedValues = currentSelectedValue ? [currentSelectedValue] : []
    return (
      <View>
        <Text
          style={styles[`label-${themeName}`] ||= {
            color: colors.textMuted,
            fontWeight: 600
          }}
          testID={`${testID}/label`}
        >
          {label}
        </Text>
        <View
          style={styles.selectWrap ||= {
            marginTop: 6
          }}
        >
          <Select
            onChangeValue={this.tt.onChangeValue}
            options={this.tt.optionsForSelect()}
            placeholder={placeholder || "—"}
            search={Boolean(search)}
            testID={testID}
            values={selectedValues}
          />
        </View>
      </View>
    )
  }

  /**
   * Returns options in the format expected by HayaSelect.
   * @returns {FormSelectOption[] | FormSelectOptionsLoader} Static options or an async option loader.
   * @throws {Error} When the options prop has an unsupported type.
   */
  optionsForSelect() {
    const {options} = this.p
    if (Array.isArray(options)) {
      return options
    }
    if (typeof options === "function") {
      return this.tt.loadOptions
    }

    throw new Error(`Expected ${this.p.name} options to be an array or function`)
  }
}

/** @type {NamedExoticComponent<FormSelectProps>} */
const FormSelectComponent = memo(shapeComponent(FormSelect))

export default FormSelectComponent

/**
 * Filters an options result to selected values when HayaSelect is resolving current async options.
 * @param {FormSelectOption[] | FormSelectOptionsResult} result Original options result.
 * @param {(string | number)[] | undefined} values Selected values requested by HayaSelect.
 * @returns {FormSelectOption[] | FormSelectOptionsResult} Filtered result when values were requested.
 */
function filterOptionsResultForValues(result, values) {
  const selectedValues = normalizedValues(values)
  if (selectedValues.length === 0) return result
  if (Array.isArray(result)) {
    return filterOptionsForValues(result, values)
  }

  const options = filterOptionsForValues(result.options, values)

  return {
    ...result,
    options,
    page: 1,
    totalCount: options.length
  }
}

/**
 * Filters option rows by selected values.
 * @param {FormSelectOption[]} options Options to filter.
 * @param {(string | number)[] | undefined} values Selected values requested by HayaSelect.
 * @returns {FormSelectOption[]} Matching options, or the original array when values are absent.
 */
function filterOptionsForValues(options, values) {
  const selectedValues = normalizedValues(values)
  if (selectedValues.length === 0) return options

  return options.filter((option) => selectedValues.includes(String(option.value)))
}

/**
 * Normalizes selected values for comparison.
 * @param {(string | number)[] | undefined} values Selected values.
 * @returns {string[]} String values.
 */
function normalizedValues(values) {
  if (!Array.isArray(values)) return []

  return values.map((value) => String(value))
}
